import React, { useCallback } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { formatDateShort } from '~/services/TimeService';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useFilteredByExpiry } from '../../shared/useFilteredByExpiry';
import type { SurchargeRoomAssignment } from '../../types';

import ManageRoomRateSurchargesAction from './ManageRoomRateSurchargesAction';
import { availabilityLabels, getAvailabilityChipColor } from './surchargeAvailability';
import { getMatchedCardSx } from './surchargeCardStyles';
import type { MatchedRoomRateCardsProps } from './types';

export default function MatchedRoomRateCards({ assignments, hideExpired, now }: MatchedRoomRateCardsProps) {
  const isExpired = useCallback(
    (assignment: SurchargeRoomAssignment) =>
      getDateRangeAvailability(assignment.reservationSurcharge, now) === 'expired',
    [now],
  );
  const visibleAssignments = useFilteredByExpiry(assignments, isExpired, hideExpired);

  if (assignments.length === 0) {
    return (
      <Box
        sx={(theme) => ({
          ...getMatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">No matching room-rate surcharge blocks.</Typography>
      </Box>
    );
  }

  if (visibleAssignments.length === 0) {
    return (
      <Box
        sx={(theme) => ({
          ...getMatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">All matched room-rate surcharge blocks are expired.</Typography>
      </Box>
    );
  }

  return (
    <>
      {visibleAssignments.map((assignment) => {
        const availability = getDateRangeAvailability(assignment.reservationSurcharge, now);
        const ratePlanLabel = [assignment.ratePlanName, assignment.ratePlanCode].filter(Boolean).join(' - ');

        return (
          <Box key={`${assignment.roomRateId}-${assignment.reservationSurcharge.id}`} sx={getMatchedCardSx}>
            <Stack
              alignItems={{ xs: 'flex-start', md: 'center' }}
              direction={{ xs: 'column', md: 'row' }}
              flexWrap="wrap"
              gap={1.5}
              rowGap={1}
            >
              <Stack alignItems="center" direction="row" spacing={0.75} sx={{ color: 'success.dark', flexShrink: 0 }}>
                <CheckCircleOutlineIcon fontSize="small" />
                <Typography fontWeight={700} variant="body2">
                  Matched
                </Typography>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Typography fontWeight={700} variant="body2">
                  {assignment.roomName}
                </Typography>
                <ManageRoomRateSurchargesAction href={assignment.href} />
              </Stack>
              <Typography color="text.secondary" sx={{ minWidth: 0 }} variant="body2">
                {ratePlanLabel} · {formatDateShort(assignment.reservationSurcharge.start_date)} to{' '}
                {formatDateShort(assignment.reservationSurcharge.end_date)} ·{' '}
                {daysOfWeekMessage(assignment.reservationSurcharge)}
              </Typography>
              <Chip
                color={getAvailabilityChipColor(availability)}
                label={availabilityLabels[availability]}
                size="small"
                sx={{ flexShrink: 0 }}
                variant="outlined"
              />
            </Stack>
          </Box>
        );
      })}
    </>
  );
}
